import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { createAppTheme } from './index';
import { useLocation } from 'react-router-dom';

interface ThemeContextType {
  mode: PaletteMode;
  toggleColorMode: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleColorMode: () => {},
});

export const useThemeContext = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const isRiverHackPage = location.pathname === '/riverhacks';

  // Initialize theme from localStorage, default to 'light'
  const [mode, setMode] = useState<PaletteMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode === 'dark' || savedMode === 'light') ? savedMode : 'light';
  });

  // Update localStorage when theme changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      mode: isRiverHackPage ? 'light' : mode,
      toggleColorMode: () => {
        if (!isRiverHackPage) {
          setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
        }
      },
    }),
    [mode, isRiverHackPage]
  );

  const theme = useMemo(() => createAppTheme(isRiverHackPage ? 'light' : mode), [mode, isRiverHackPage]);

  return (
    <ThemeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 